import {_} from 'lodash';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {IObjectData, IRouteDefinitionParams, PartialRecord} from '../../../index';
import {INPUT_DATE_FORMAT} from "../../../constants";
import {global} from "../../../appContext";
import {template as tpl, UserWizardMutable} from "./mutable";
import {createErrorMessage, getRawSyncFieldValue, isNullOrUndefinedOrEmptyString} from "../../../util/misc";
import {setRouteHash} from "../../../constants/RouteDefinitions";
import {nsUserWizard} from "./nsUserWizard";
import {convertFromServerString} from "../../../util/datetime";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {PersonaliaModelEdit} from "./PersonaliaModelEdit";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../../constants/Language";
import {TOperationType} from "./index";
import Step = nsUserWizard.Step;
import TWizardSyncfield = nsUserWizard.TWizardSyncfield;
import getCountByString = nsSyncFields.getCountByString;

export let template = tpl;

export class viewModel extends UserWizardMutable {

    private readonly reqGetObject: PromiseRequest = global.getPromiseRequest('getobject')
    protected readonly reqSubmit: PromiseRequest = global.getPromiseRequest('updateobject')
    protected readonly operationType: TOperationType = 'edit'

    constructor(params: IRouteDefinitionParams) {
        super(params)
        this.init()
    }

    protected override init(): Promise<any> {
        this.isReady(false)
        this.messageData({message: REQUEST_MESSAGE})
        this.step(null);
        this.personaliaModel = new PersonaliaModelEdit({dataset: this.dataset})
        return Promise.resolve()
            .then(() => this.__requestRouteData?.request()
                .then((data: IObjectData[]) => {
                    this.dataset.original = data[0] ?? null
                })
            )
            .then(() => super.init())
            .then(() => {
                this.initData()
                this.step(Step.Personalia)
                this.messageData(null)
                this.isReady(true)
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))))
    }

    private initData() {

        // --- PERSONALIA ----------------------------------------------------------------------------------------------
        this.personaliaModel.Attributes.forEach(key => this.personaliaModel.attr[key]?.(this.getOriginalValue(key)))


        // --- PRIMARY POSITION ----------------------------------------------------------------------------------------
        const primaryIndex = this.getOriginalValue('APrimaryPosition')?.split('|').findIndex(str => str.toLowerCase() == 'true')

        const department = this.getOriginalValue('DepartmentNumber')
            ? this.dataset.departments?.find(o => getRawSyncFieldValue('OrgUnitnr', o.syncfields) == this.getOriginalValue('DepartmentNumber'))
            : null
        const startDate = this.getOriginalValue('APositionStartDate')?.split('|')?.[primaryIndex] ?? this.getOriginalValue('Start')
        const start = startDate ? convertFromServerString(startDate).format(INPUT_DATE_FORMAT) : null
        const endDate = this.getOriginalValue('APositionEndDate')?.split('|')?.[primaryIndex] ?? this.getOriginalValue('ExpiryDate') ?? this.getOriginalValue('Stop')
        const end = endDate ? convertFromServerString(endDate).format(INPUT_DATE_FORMAT) : null

        this.primaryPositionModel.initMeta({
            department,
            end,
            start,
            isNoEndDate: !end,
            isNoStartDate: !start,
        })

        this.primaryPositionModel.initAttr({
            Title: this.getOriginalValue('Title'),
        })

        if (getCountByString(this.getOriginalValue('APrimaryPosition')) <= 1) {
            return // no secondary positions
        }

        // --- SECONDARY POSITIONS -------------------------------------------------------------------------------------
        const numPositions = getCountByString(this.getOriginalValue('APrimaryPosition'))

        for (let i = 0; i < numPositions; i++) {
            if (i == primaryIndex) {
                continue
            }
            const model = this.generatePositionModel('secondary')
            const keys: TWizardSyncfield[] = [
                'APositionCodeName', // Title
                'APositionTypeName', // Title
                'ParentDepartmentNumber', // DepartmentNumber
                'AUnitId', // DepartmentNumber
                'APositionStartDate', // Start
                'APositionEndDate', // ExpiryDate
                'APrimaryPosition', // True|False
            ]
            const obj: PartialRecord<TWizardSyncfield, string> = {}
            keys.forEach(k => {
                obj[k] = this.getOriginalValue(k)?.split('|')[i] ?? ''
            })
            model.attr.Title?.(obj.APositionCodeName ?? obj.APositionTypeName ?? '')
            model.meta.department(
                (obj.ParentDepartmentNumber ?? obj.AUnitId)
                    ? this.dataset.departments?.find(o => getRawSyncFieldValue('OrgUnitnr', o.syncfields) == (obj.ParentDepartmentNumber ?? obj.AUnitId)) ?? null
                    : null
            )
            if (obj.APositionStartDate) {
                model.meta.start(convertFromServerString(obj.APositionStartDate).format(INPUT_DATE_FORMAT))
            }
            if (obj.APositionEndDate) {
                model.meta.end(convertFromServerString(obj.APositionEndDate).format(INPUT_DATE_FORMAT))
            }
            model.meta.isNoStartDate(!model.meta.start())
            model.meta.isNoEndDate(!model.meta.end())

            this.secondaryPositionModels.push(model)
        }

        const models = [this.primaryPositionModel, ...this.secondaryPositionModels()]
        nsUserWizard.WizardOptionalPositionSyncfieldNames.forEach(k => {
            const originalValue = this.getOriginalValue(k)
            if (!isNullOrUndefinedOrEmptyString(originalValue)) {
                models.forEach((model, i) => {
                    const value = originalValue?.split('|')[i]
                    model.optionalSyncfieldObservables[k] = ko.observable(value)
                })
                const sf = this.dataset.syncfields()?.find(o => o.name == k)
                if (sf) {
                    this.optionalSyncfields.push(sf)
                }
            }
        })
    }

    private getOriginalValue(key: nsUserWizard.TWizardSyncfield): string {
        return getRawSyncFieldValue(key, this.dataset.original?.syncfields ?? []) ?? ''
    }

    protected onSubmit() {
        const original = this.dataset.original
        if (!original) {
            return
        }
        const data = _.defaultsDeep({id: original.id}, super.getSubmitData());
        this.messageData({message: UPDATING_MESSAGE});
        this.reqSubmit.abort();
        this.reqSubmit
            .request({
                jobject: JSON.stringify(data)
            })
            .then((data: IObjectData[]) => {
                global.cacheManager.removeCache('getruleset');
                global.cacheManager.removeCache('searchruleset');
                global.cacheManager.removeCache('searchobject');
                global.cacheManager.removeCache('getobject');
                this.dataset.original = data[0] ?? null;
                setRouteHash('manualuserdetails', {id: original.id})
                global.snackbarData({message: `subject is updated|s:((manual user))`})
            })
            .catch((reason: string) => {
                this.step(Step.Error);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

}